import { CreateNewFolder, People, PersonAdd, CheckCircle, Cancel, AdminPanelSettings, Person, MoreVert, Edit, Send } from "@mui/icons-material";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Card, CardContent, Chip, Avatar, Alert, CircularProgress, TablePagination, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { useContext, useState, useEffect } from "react";
import { User } from "../../../Models/User";
import { ADMIN_VIEWS, defaultUser } from "../../../utils/constant";
import { Role } from "../../../utils/type";
import AdminUserEditingModal from '../modal/AdminUserEditingModal';
import { AdminUserContext } from "../../../contexts/AdminUserContext";
import useAxios from "../../../hooks/useAxios";
import "../../../styles/GlobalUserListTable.css";
import {CommonContext} from "../../../contexts/CommonContext";
import { logger } from '../../../utils/logger';


export default function GlobalUserListTable(props: { selectedMenu: Function }) {
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [openedUser, setOpenedUser] = useState<User>(new User(defaultUser));

    // Menu contextuel
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuUser, setMenuUser] = useState<User | null>(null);
    const [resendingEmail, setResendingEmail] = useState<boolean>(false);

    // API state
    const [users, setUsers] = useState<User[]>([]);
    const { needToRefreshData, setNeedToRefreshData } = useContext(CommonContext);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>('');

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const { updateUsers } = useContext(AdminUserContext);
    const { getWithAxios, postWithAxios } = useAxios();

    // Fetch users from API
        const fetchUsers = async () => {
            setIsLoading(true);
            setError('');

            try {
                const params = new URLSearchParams();
                params.append("page", String(page + 1)); // Backend page commence à 1
                params.append("limit", String(rowsPerPage));

                const url = `/admin/users?${params.toString()}`;
                const usersData = await getWithAxios(url);

                // Handle response structure (check if data is nested)
                const usersList = Array.isArray(usersData.data) ? usersData.data : [];

                setUsers(usersList);
                setTotalCount(usersData.pagination?.total || usersList.length);
                updateUsers(usersList); // Update context if needed
            } catch (err) {
                logger.error('Error fetching users:', err);
                setError(err instanceof Error ? err.message : 'Erreur lors du chargement des utilisateurs');
            } finally {
                setIsLoading(false);
            }
        };
    useEffect(() => {
        fetchUsers();
    }, [page, rowsPerPage]);

    useEffect(() => {
        if (needToRefreshData) {
            fetchUsers().then(() => setNeedToRefreshData(false));
        }
    }, [needToRefreshData]);

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Menu contextuel handlers
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setMenuUser(user);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuUser(null);
    };

    const handleEditUser = () => {
        if (menuUser) {
            setOpenedUser(menuUser);
            setOpenModal(true);
        }
        handleMenuClose();
    };

    const handleResendActivation = async () => {
        if (!menuUser) return;

        setResendingEmail(true);
        try {
            await postWithAxios('/auth/resend-activation', { email: menuUser.email });
            logger.info(`Activation email resent to ${menuUser.email}`);
            // TODO: Afficher une notification de succès
        } catch (err) {
            logger.error('Error resending activation email:', err);
            // TODO: Afficher une notification d'erreur
        } finally {
            setResendingEmail(false);
            handleMenuClose();
        }
    };

    const statsData = [
        {
            label: "Total utilisateurs",
            value: users?.length || 0,
            subtitle: "comptes créés",
            icon: <People />,
            type: "default"
        },
        {
            label: "Comptes actifs",
            value: users.filter(it => it.isActivated).length,
            subtitle: "utilisateurs connectés",
            icon: <CheckCircle />,
            type: "success"
        },
        {
            label: "Comptes inactifs",
            value: users.filter(it => !it.isActivated).length,
            subtitle: "en attente d'activation",
            icon: <Cancel />,
            type: "error"
        },
        {
            label: "Administrateurs",
            value: users.filter(it => it.role === Role.ADMIN).length,
            subtitle: "privilèges adminService",
            icon: <AdminPanelSettings />,
            type: "info"
        }
    ];

    const handleOnClick = (row: User) => {
        setOpenedUser(row);
        setOpenModal(true);
    };

    const handleNewViewClick = (view: number) => {
        return () => props.selectedMenu(view);
    };

    const handleUserUpdate = (updatedUser: User) => {
        setUsers(prevUsers =>
            prevUsers.map(user =>
                user.uuid === updatedUser.uuid ? updatedUser : user
            )
        );
        updateUsers(users); // Sync with context
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="users-container">
                <div className="section-header">
                    <div className="header-left">
                        <div className="section-icon">
                            <People />
                        </div>
                        <h2 className="section-title">Gestion des utilisateurs</h2>
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                    <CircularProgress />
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="users-container">
                <div className="section-header">
                    <div className="header-left">
                        <div className="section-icon">
                            <People />
                        </div>
                        <h2 className="section-title">Gestion des utilisateurs</h2>
                    </div>
                </div>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            </div>
        );
    }

    return (
        <div className="users-container">
            {/* En-tête de section */}
            <div className="section-header">
                <div className="header-left">
                    <div className="section-icon">
                        <People />
                    </div>
                    <h2 className="section-title">Gestion des utilisateurs</h2>
                </div>
                <button className="add-user-button" onClick={handleNewViewClick(ADMIN_VIEWS.NEW_USER_VIEW)}>
                    <PersonAdd />
                    Nouvel utilisateur
                </button>
            </div>

            {/* Statistiques */}
            <div className="stats-grid">
                {statsData.map((stat, index) => (
                    <Card key={`stat-${index}`} className={`stat-card ${stat.type}`}>
                        <CardContent className="stat-content">
                            <div className="stat-header">
                                <div>
                                    <h4 className="stat-label">{stat.label}</h4>
                                    <h2 className="stat-value">{stat.value}</h2>
                                    <p className="stat-subtitle">{stat.subtitle}</p>
                                </div>
                                <div className={`stat-icon-container ${stat.type}`}>
                                    {stat.icon}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Table des utilisateurs */}
            <Card className="users-table-card">
                {/*<div className="table-header">*/}
                {/*    <h3 className="table-title">Liste des utilisateurs</h3>*/}
                {/*    <p className="table-subtitle">Cliquez sur un utilisateur pour modifier ses informations</p>*/}
                {/*</div>*/}

                {users.length === 0 ? (
                    <div style={{ padding: '2rem', textAlign: 'center' }}>
                        <People style={{ fontSize: '3rem', color: '#ccc', marginBottom: '1rem' }} />
                        <p>Aucun utilisateur trouvé</p>
                    </div>
                ) : (
                    <>
                        <TableContainer className="styled-table-container">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Utilisateur</TableCell>
                                        <TableCell>Statut du compte</TableCell>
                                        <TableCell>Rôle</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map((user) => (
                                        <TableRow
                                            key={user.uuid}
                                            onClick={() => handleOnClick(user)}
                                        >
                                            <TableCell>
                                                <div className="user-cell">
                                                    <Avatar className="user-avatar">
                                                        <Person />
                                                    </Avatar>
                                                    <div className="user-info">
                                                        <div className="user-name">{user.firstname} {user.lastname}</div>
                                                        <div className="user-email">{user.email}</div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    className="status-chip status-user"
                                                    label={user.isActivated ? "Actif" : "Inactif"}
                                                    color={user.isActivated ? "success" : "error"}
                                                    variant="filled"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    className="status-chip"
                                                    label={user.role === Role.ADMIN ? "Administrateur" : "Participant"}
                                                    color={user.role === Role.ADMIN ? "primary" : "default"}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleMenuOpen(e, user)}
                                                    sx={{
                                                        color: 'var(--text-secondary)',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                                                            color: 'var(--primary-color)',
                                                        }
                                                    }}
                                                >
                                                    <MoreVert />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={totalCount}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Lignes par page:"
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        />
                    </>
                )}
            </Card>

            <AdminUserEditingModal
                openModal={openModal}
                setOpenModal={setOpenModal}
                openedUser={openedUser}
                setOpenedUser={setOpenedUser}
                onUserUpdate={handleUserUpdate}
            />

            {/* Menu contextuel */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    sx: {
                        minWidth: 200,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        borderRadius: '12px',
                        mt: 1
                    }
                }}
            >
                <MenuItem onClick={handleEditUser}>
                    <ListItemIcon>
                        <Edit fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Modifier</ListItemText>
                </MenuItem>
                {menuUser && !menuUser.isActivated && [
                    <Divider key="divider" />,
                    <MenuItem
                        key="resend"
                        onClick={handleResendActivation}
                        disabled={resendingEmail}
                    >
                        <ListItemIcon>
                            <Send fontSize="small" sx={{ color: resendingEmail ? 'inherit' : 'var(--primary-color)' }} />
                        </ListItemIcon>
                        <ListItemText>
                            {resendingEmail ? 'Envoi en cours...' : "Renvoyer le mail d'activation"}
                        </ListItemText>
                    </MenuItem>
                ]}
            </Menu>
        </div>
    );
}
